/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { QrReader } from "react-qr-reader";

const endPoint = process.env.REACT_APP_API_BASE_URL;

export default function QRScanner() {
  const [scannedCode, setScannedCode] = useState("");
  const [status, setStatus] = useState("📡 Ready for next scan");
  const [mode, setMode] = useState("hardware");
  const [role, setRole] = useState("");
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("qr_token");
    const roleFromStorage = localStorage.getItem("qr_role");

    if (!token || (roleFromStorage !== "USER" && roleFromStorage !== "ADMIN")) {
      navigate("/qr-attendance-login");
      return; // ✅ fix for consistent-return
    }
    setRole(roleFromStorage);
  }, [navigate]);

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  };

  useEffect(() => {
    if (mode === "hardware") {
      focusInput();
      const handleWindowFocus = () => {
        setTimeout(() => focusInput(), 50);
      };
      window.addEventListener("focus", handleWindowFocus);
      return () => window.removeEventListener("focus", handleWindowFocus);
    }
    return undefined;
  }, [mode]);

  const hitApi = async (code) => {
    try {
      setStatus("⏳ Marking attendance...");
      const token = localStorage.getItem("qr_token");
      const qrUserName = localStorage.getItem("qr_user_name");

      const { data } = await axios.get(
        `${endPoint}/api/mark-attendance?encryptData=${code}&markedBy=${qrUserName}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStatus(`✅ ${data}`);
      setScannedCode("");
      if (mode === "hardware") focusInput();
    } catch (err) {
      if (err.response) {
        setStatus(
          `❌ Error: ${
            err.response.data?.message || JSON.stringify(err.response.data)
          }`
        );
      } else if (err.request) {
        setStatus("❌ No response from server. Check connection.");
      } else {
        setStatus("❌ Request failed");
      }
      if (mode === "hardware") focusInput();
    }
  };

  useEffect(() => {
    if (scannedCode.trim() !== "" && mode === "hardware") {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => hitApi(scannedCode.trim()), 1500);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [scannedCode, mode]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTimeout(() => focusInput(), 30);
    }
  };

  const handleClearFocus = () => {
    setScannedCode("");
    focusInput();
    setStatus("📡 Ready for next scan");
  };

  const handleLogout = () => {
    localStorage.removeItem("qr_token");
    localStorage.removeItem("qr_token_expiry");
    localStorage.removeItem("qr_role");
    localStorage.removeItem("qr_user_name");
    navigate("/qr-attendance-login");
  };

  // ✅ Helper function to avoid nested ternary
  const getStatusColor = () => {
    if (status.includes("✅")) return "#16a34a";
    if (status.includes("❌")) return "#dc2626";
    return "#2563eb";
  };

  return (
    <div
      style={{
        background: "#f9fafb",
        color: "#111",
        minHeight: "100vh",
        padding: "20px",
        fontFamily: "monospace",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: "20px",
          gap: "10px",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: "bold" }}>
          📡 QR Attendance Scanner
        </h1>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {role === "ADMIN" && (
            <button
              type="button"
              onClick={() => navigate("/qr-attendance-list")}
              style={{
                background: "#3b82f6",
                border: "none",
                borderRadius: "8px",
                padding: "10px 15px",
                color: "#fff",
                cursor: "pointer",
                fontWeight: "bold",
                minWidth: "130px",
              }}
            >
              📋 Attendance List
            </button>
          )}
          <button
            type="button"
            onClick={handleLogout}
            style={{
              background: "#ef4444",
              border: "none",
              borderRadius: "8px",
              padding: "10px 15px",
              color: "#fff",
              cursor: "pointer",
              fontWeight: "bold",
              minWidth: "100px",
            }}
          >
            🚪 Logout
          </button>
        </div>
      </div>

      {/* Mode Toggle */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={() => setMode("hardware")}
          style={{
            flex: "1",
            padding: "12px",
            background: mode === "hardware" ? "#22c55e" : "#e5e7eb",
            color: mode === "hardware" ? "#fff" : "#000",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          🖥️ Hardware Scanner
        </button>
        <button
          type="button"
          onClick={() => setMode("mobile")}
          style={{
            flex: "1",
            padding: "12px",
            background: mode === "mobile" ? "#22c55e" : "#e5e7eb",
            color: mode === "mobile" ? "#fff" : "#000",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          📱 Mobile Camera or Webcam
        </button>
      </div>

      {/* Scanner Section */}
      {mode === "hardware" ? (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              marginTop: "20px",
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={scannedCode}
              onChange={(e) => setScannedCode(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="🔍 Place your QR code and scan..."
              style={{
                background: "#fff",
                border: "1px solid #d1d5db",
                borderRadius: "10px",
                padding: "14px",
                color: "#111",
                minHeight: "140px",
                fontSize: "1rem",
                outline: "none",
                boxShadow: "0 0 6px rgba(0,0,0,0.05)",
              }}
            />

            <div
              style={{
                background: "#fff",
                color: getStatusColor(), // ✅ no nested ternary
                borderRadius: "10px",
                padding: "14px",
                textAlign: "center",
                fontWeight: "bold",
                boxShadow: "0 0 6px rgba(0,0,0,0.1)",
                minHeight: "80px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {status}
            </div>
          </div>

          <button
            type="button"
            onClick={handleClearFocus}
            style={{
              marginTop: "20px",
              width: "100%",
              padding: "12px 20px",
              background: "#3b82f6",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              fontWeight: "bold",
              color: "#fff",
            }}
          >
            Refresh
          </button>
        </>
      ) : (
        <>
          <div
            style={{
              width: "100%",
              maxWidth: "400px",
              margin: "0 auto",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 0 8px rgba(0,0,0,0.2)",
            }}
          >
            <QrReader
              onResult={(result) => {
                if (result) {
                  hitApi(result?.text);
                }
              }}
              constraints={{ facingMode: "environment" }}
              style={{ width: "100%" }}
            />
          </div>

          <div
            style={{
              marginTop: "20px",
              padding: "14px",
              background: "#fff",
              color: getStatusColor(), // ✅ no nested ternary
              borderRadius: "10px",
              textAlign: "center",
              fontWeight: "bold",
              boxShadow: "0 0 6px rgba(0,0,0,0.1)",
            }}
          >
            {status}
          </div>
        </>
      )}
    </div>
  );
}
